/**
 * The body of a mailbox email, rendered as the sender formatted it (TRUS-760).
 *
 * The reading pane used to strip the HTML with a regex, which lost every line
 * break and table and left the text of <style> blocks and Outlook's
 * "From: / Sent: / To:" reply headers run together on screen.
 *
 * Mail is untrusted, so it is never put into the app's own DOM:
 *  - DOMPurify removes scripts, event handlers, javascript: URLs, forms, embeds,
 *    <meta> (a meta refresh could send the frame to a look-alike sign-in page)
 *    and <base>;
 *  - it renders in an iframe sandboxed WITHOUT allow-scripts, so nothing in it
 *    can run. allow-same-origin only lets this component measure the content to
 *    size the frame; without scripts the mail cannot use it;
 *  - the frame's own CSP blocks every network fetch except, once the user asks,
 *    https images. Remote images stay off by default (they are how senders track
 *    opens), with a "Show images" button;
 *  - links open in a new tab (allow-popups-to-escape-sandbox), never in the frame.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { ImageOff } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

const FORBID_TAGS = ['meta', 'base', 'link', 'form', 'input', 'button', 'textarea', 'select', 'iframe', 'frame', 'object', 'embed', 'script'];

/** True when the mail loads images (or CSS backgrounds) from the network. */
export function hasRemoteImages(html: string): boolean {
  return /<img\b[^>]*\bsrc\s*=\s*["']?\s*https?:/i.test(html) || /url\(\s*["']?\s*https?:/i.test(html);
}

/** The sanitized, self-contained document the iframe shows. */
export function buildEmailDocument(html: string, { allowRemoteImages }: { allowRemoteImages: boolean }): string {
  const purify = DOMPurify();
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  const clean = purify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    FORBID_TAGS,
    FORBID_ATTR: ['srcset', 'action', 'formaction', 'ping'],
  }) as string;

  const doc = new DOMParser().parseFromString(clean, 'text/html');
  const imgSrc = allowRemoteImages ? 'data: cid: https:' : 'data: cid:';
  const head = doc.head;
  const csp = doc.createElement('meta');
  csp.setAttribute('http-equiv', 'Content-Security-Policy');
  csp.setAttribute('content', `default-src 'none'; style-src 'unsafe-inline'; img-src ${imgSrc}; font-src data:`);
  const base = doc.createElement('base');
  base.setAttribute('target', '_blank');
  const style = doc.createElement('style');
  style.textContent =
    'html,body{margin:0;padding:0;background:#fff;color:#1f2937;}' +
    'body{font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
    'overflow-wrap:anywhere;padding:2px;}' +
    'img{max-width:100%;height:auto;}table{max-width:100%;}pre{white-space:pre-wrap;}' +
    'blockquote{margin:0 0 0 .5em;padding-left:.75em;border-left:2px solid #d1d5db;color:#4b5563;}';
  // The CSP must come first so it applies before anything else in the document loads.
  head.prepend(csp, base, style);
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

export function EmailHtmlBody({ html, title }: { html: string; title: string }) {
  const { t } = useAppTranslation();
  const [showImages, setShowImages] = useState(false);
  const [height, setHeight] = useState(200);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const remote = useMemo(() => hasRemoteImages(html), [html]);
  const srcDoc = useMemo(() => buildEmailDocument(html, { allowRemoteImages: showImages }), [html, showImages]);

  // A different email starts with images off again.
  useEffect(() => setShowImages(false), [html]);

  const measure = () => {
    const doc = frameRef.current?.contentDocument;
    if (!doc?.documentElement) return;
    setHeight(Math.max(80, Math.ceil(doc.documentElement.scrollHeight)));
  };

  const onLoad = () => {
    measure();
    const doc = frameRef.current?.contentDocument;
    if (doc?.body && typeof ResizeObserver !== 'undefined') {
      // Images arriving after load change the height.
      const ro = new ResizeObserver(measure);
      ro.observe(doc.body);
      frameRef.current?.addEventListener('load', () => ro.disconnect(), { once: true });
    }
  };

  return (
    <div data-testid="email-html-body">
      {remote && !showImages && (
        <div className="mb-2 flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
          <ImageOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="flex-1">
            {t('inbox.email.imagesHidden', 'Images from the sender are hidden to protect your privacy.')}
          </span>
          <button
            type="button"
            onClick={() => setShowImages(true)}
            className="font-medium text-blue-600 hover:underline"
            data-testid="email-show-images"
          >
            {t('inbox.email.showImages', 'Show images')}
          </button>
        </div>
      )}
      <iframe
        ref={frameRef}
        title={title}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        srcDoc={srcDoc}
        onLoad={onLoad}
        style={{ height }}
        className="w-full rounded border border-gray-200 bg-white dark:border-gray-700"
      />
    </div>
  );
}

export default EmailHtmlBody;
